import { Location } from '@angular/common';
import { Inject, Injectable, InjectionToken, OnDestroy, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, UrlSegment } from '@angular/router';
import { RouteService } from '@app/core/route.service';
import { AppRouteTranslationMap } from '@app/shared/app.route.enum';
import { __ } from '@app/shared/functions/object.functions';
import { of, Subject } from 'rxjs';
import { filter, mergeMap, takeUntil } from 'rxjs/operators';

import { Logger } from '../logger/logger.service';

const log = new Logger('BreadcrumbComponent');

export interface WithBreadcrumbs {
  setBreadCrumb(): void;
}

export class BreadcrumbSegment {
  title!: string;
  routerLink!: string;
  path!: string;
  viewName!: string;
}

export const APP_ROUTE = new InjectionToken<string>('AppRoute');
export const APP_TITLE = new InjectionToken<string>('AppTitle');

/**
 * The breadcrumb service creates breadcrumbs by subscribing to the urlSegments of the active route.
 * Breadcrumb state itself is synchronous UI state and is exposed as a readonly signal.
 *
 * WARNING: - DOES NOT WORK ON SAME ROUTE NAVIGATION (COMPONENT WILL NOT BE INITIALIZED AGAIN WHEN E.G.
 *            ONLY THE ID PARAMETER CHANGES IN A ROUTE).
 *          - MAY NOT WORK WITH MORE A GREATER HIEARCHY THAN E.G. limited/editions/3/edit due to
 *            this.breadcrumbs.slice
 */
@Injectable()
export class BreadcrumbsService implements OnDestroy {
  private readonly breadcrumbSegmentsState = signal<BreadcrumbSegment[]>([]);
  readonly breadcrumbSegments = this.breadcrumbSegmentsState.asReadonly();

  private currentUrl: string = '';
  private destroySubject?: Subject<void>;

  private get destroy$(): Subject<void> {
    return this.destroySubject ??= new Subject<void>();
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    @Inject(APP_ROUTE) private AppRoute: any,
    @Inject(APP_TITLE) private appTitleKey: string
  ) {
    if (__.IsNullOrUndefined(this.AppRoute)) {
      throw new Error(
        'The injection token APP_ROUTE has not been provided. The breadcrumbs service cannot be used without it.'
      );
    }

    this.init();
  }

  ngOnDestroy(): void {
    this.destroySubject?.next();
    this.destroySubject?.complete();
  }

  /**
   * Adds a new breadcrumb to the list of breadcrumbs based on the provided title, optional identifier and optional view name.
   * If the identifier is left empty, no routing link will be produced for navigation calculation. The view name can be used
   * to further specify the current breadcrumb with e.g sub navigation names, which will be displayed without a router link
   * behind the provided title.
   *
   * @param title The title that will be displayed in the breadcrumb list
   * @param identifier The optional identifier that will be used for routing purposes
   * @param viewName The optional view name to further specify sub navigation
   */
  addBreadcrumb(title: string, identifier?: string, viewName?: string): void {
    if (__.IsNullOrUndefinedOrEmpty(title)) {
      log.error('The title of a breadcrumb has not been set');
      return;
    }

    const current = this.breadcrumbSegmentsState();
    const _identifier = identifier || null;
    const _viewName = viewName || null;

    if (current.findIndex(q => q.title === title && q.path === identifier) > -1) {
      log.error('The same breadcrumb cannot be added twice');
      return;
    }

    const routerLink = __.IsNullOrUndefinedOrEmpty(identifier)
      ? null
      : `${this.buildRouterLinkFromPreviousBreadcrumbPaths(current)}${identifier}`;

    const segment = Object.assign(new BreadcrumbSegment(), {
      routerLink,
      title,
      path: _identifier,
      viewName: _viewName
    } as BreadcrumbSegment);

    this.breadcrumbSegmentsState.set([...current, segment]);
  }

  removeLast(): void {
    this.breadcrumbSegmentsState.update(segments => segments.slice(0, -1));
  }

  private init(): void {
    const root = Object.assign(new BreadcrumbSegment(), {
      routerLink: '/',
      title: this.appTitleKey,
      path: '/'
    } as BreadcrumbSegment);

    this.breadcrumbSegmentsState.set([root]);

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        mergeMap(() => of(RouteService.MergeUrlSegments(this.route.snapshot))),
        takeUntil(this.destroy$)
      )
      .subscribe(this.calculateUrlSegments.bind(this));
  }

  private calculateUrlSegments(urlSegments: UrlSegment[]): void {
    if (this.currentUrl === this.location.path()) {
      return;
    }

    this.currentUrl = this.location.path();

    // Depending on how many url segments there are now, only remove n - 1.
    const segments = this.breadcrumbSegmentsState().slice(0, 1);

    // The first url segment should be the resource (based on REST).
    if (!__.IsNullOrUndefined(urlSegments[0])) {
      const validRouteKeys: string[] = Object.keys(this.AppRoute);
      const validRoutes = validRouteKeys.map((k: string) => (this.AppRoute as Record<string, any>)[k]).map((v: any) => v);

      if (validRoutes.find(q => q === urlSegments[0].path)) {
        segments.push(
          Object.assign(new BreadcrumbSegment(), {
            routerLink: `${this.buildRouterLinkFromPreviousBreadcrumbPaths(segments)}${urlSegments[0].path}`,
            title: this.getTitleOfUrlPath(urlSegments[0].path),
            path: urlSegments[0].path
          }) as BreadcrumbSegment
        );
      } else {
        log.warn(`The route ${urlSegments[0]} is not a valid route of the AppRoute enum`);
      }
    }
    // The rest of the url segments have to be handled in the components using the breadcrumb service.

    this.breadcrumbSegmentsState.set(segments);
  }

  private getTitleOfUrlPath(path: string): string {
    return AppRouteTranslationMap.Translations[path];
  }

  private buildRouterLinkFromPreviousBreadcrumbPaths(segments: BreadcrumbSegment[] = this.breadcrumbSegmentsState()): string {
    let path = ``;

    for (const segment of segments) {
      if (!__.IsNullOrUndefinedOrEmpty(segment.path)) {
        path = `${path}${segment.path}/`;
      }
    }

    return path;
  }
}
